/**
 * Execution modality — FE mirror of the backend canonical vocabulary.
 *
 * Legible Modality Grove sprint (2026-07-06), docs/plans/legible-modality-grove.md.
 * Backend SSOT: services/execution_modality.py (vocabulary + validation).
 * API surface: workout rows carry `executionModality: { mode, primary,
 * alternatives } | null` (user_workouts.execution_modality, migration 1662).
 *
 * NULL/undefined = legacy row → every consumer falls back to the pre-sprint
 * workout_type/activity_type behavior. Unknown primary values map to the
 * neutral 'cross-training' icon rather than guessing a sport.
 */

export type ExecutionModalityMode = 'prescribed' | 'flexible';

export interface ExecutionModality {
  mode: ExecutionModalityMode;
  primary: string;
  alternatives: string[];
}

/** Canonical modality → WorkoutIconType (see *WorkoutIconMapper). */
const MODALITY_ICON_MAP: Record<string, string> = {
  cycling_indoor: 'cycling',
  cycling_outdoor: 'cycling',
  running: 'running',
  treadmill_running: 'running',
  walking: 'walking',
  hiking: 'walking',
  rowing: 'rowing',
  // A fixed indoor erg, like the arm erg (endurance PR-6a).
  skierg: 'cross-training',
  arm_ergometer: 'cross-training',
  elliptical: 'cross-training',
  stair_stepper: 'cross-training',
  swimming: 'swimming',
  pool_running: 'swimming',
  strength: 'weightlifting',
  bodyweight: 'bodyweight',
  yoga: 'yoga',
  pilates: 'yoga',
  mobility: 'stretching',
  climbing: 'climbing',
  // Existing glyph names only — a NEW icon would break the widget<->app symbol
  // parity gate, and new artwork is a design decision. 'snow-sports' is already a
  // first-class member (colour token + figure.snowboarding SF Symbol + widget case),
  // so ski/snow cost nothing. Paddle craft ride 'rowing' — the same family choice
  // native_health_ingester and cardio_units already make.
  // Both are non-generic, so they resolve mode 'override' rather than 'defer'.
  paddling: 'rowing',
  kayaking: 'rowing',
  canoeing: 'rowing',
  stand_up_paddleboarding: 'rowing',
  skiing: 'snow-sports',
  snowboarding: 'snow-sports',
  sport_play: 'team-sports',
  other: 'cross-training',
};

/** Short user-facing labels for card/detail copy ("Your pick: Bike · Rowing"). */
export const MODALITY_LABELS: Record<string, string> = {
  cycling_indoor: 'Indoor bike',
  cycling_outdoor: 'Outdoor ride',
  running: 'Run',
  treadmill_running: 'Treadmill run',
  walking: 'Walk',
  hiking: 'Hike',
  rowing: 'Rowing',
  skierg: 'SkiErg',
  arm_ergometer: 'Arm ergometer',
  elliptical: 'Elliptical',
  stair_stepper: 'Stair stepper',
  swimming: 'Swim',
  pool_running: 'Pool running',
  strength: 'Strength',
  bodyweight: 'Bodyweight',
  yoga: 'Yoga',
  pilates: 'Pilates',
  mobility: 'Mobility',
  climbing: 'Climbing',
  paddling: 'Paddle',
  kayaking: 'Kayak',
  canoeing: 'Canoe',
  stand_up_paddleboarding: 'Paddleboard',
  skiing: 'Ski',
  snowboarding: 'Snowboard',
  sport_play: 'Sport',
  other: 'Cardio',
};

/**
 * Normalize whatever the caller has (the object, a JSON string from an
 * unmapped API path, or null) into an ExecutionModality or null.
 */
export function parseExecutionModality(raw: unknown): ExecutionModality | null {
  let obj: any = raw;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object' || typeof obj.primary !== 'string' || !obj.primary) {
    return null;
  }
  const mode: ExecutionModalityMode = obj.mode === 'flexible' ? 'flexible' : 'prescribed';
  const alternatives = Array.isArray(obj.alternatives)
    ? obj.alternatives.filter((a: unknown): a is string => typeof a === 'string' && a !== obj.primary)
    : [];
  return { mode, primary: obj.primary, alternatives: mode === 'flexible' ? alternatives.slice(0, 3) : [] };
}

/**
 * Icon type for a modality primary, or null when unknown/absent (caller
 * falls back to the workout_type/activity_type chain).
 */
export function modalityIconType(modality: ExecutionModality | string | null | undefined): string | null {
  if (!modality) return null;
  const primary = typeof modality === 'string' ? modality : modality.primary;
  if (!primary) return null;
  return MODALITY_ICON_MAP[primary] ?? null;
}

/**
 * Generic modality buckets that must NOT override a specific workout_type.
 * `sport_play` → team-sports and `other` → cross-training are catch-alls:
 * before 2026-07-20 the backend had no `climbing` value, so a rock-climbing
 * session got stamped `sport_play` and Priority-0 "modality always wins"
 * rendered it as a ball court (basketball on Android). A generic bucket is
 * not a precise-enough signal to clobber a specific workout_type.
 */
const GENERIC_MODALITY_ICON_TYPES = new Set<string>(['team-sports', 'cross-training']);

export type ModalityIconPrecedence = {
  /** The modality-derived icon type, or null when no modality is present. */
  icon: string | null;
  /**
   * How it should interact with the workout_type chain:
   *   'override' — specific modality (bike, swim, climbing…): use `icon` outright.
   *   'defer'    — generic bucket: run the workout_type chain; use `icon` only
   *                as a floor when the type carries no signal.
   *   'none'     — no modality present.
   */
  mode: 'override' | 'defer' | 'none';
};

/**
 * Precedence of a modality-derived icon over the workout_type/title chain.
 * The single source of truth consumed by BOTH getWorkoutIconType mappers
 * (native + web) so the dot and the card can never disagree on the rule.
 */
export function modalityIconPrecedence(
  modality: ExecutionModality | string | null | undefined,
): ModalityIconPrecedence {
  const icon = modalityIconType(modality);
  if (!icon) return { icon: null, mode: 'none' };
  return { icon, mode: GENERIC_MODALITY_ICON_TYPES.has(icon) ? 'defer' : 'override' };
}

/** "Bike · Rowing · Arm ergometer" choice-set line for flexible sessions; null otherwise. */
export function modalityChoiceLine(modality: ExecutionModality | null | undefined): string | null {
  if (!modality || modality.mode !== 'flexible') return null;
  const names = [modality.primary, ...modality.alternatives]
    .map((m) => MODALITY_LABELS[m] || m)
    .filter(Boolean);
  if (names.length < 2) return null;
  return names.join(' · ');
}
